import React from 'react';
import {
  VStack,
  Box,
  CircularProgress,
  Button,
  Text,
  Link,
} from '@chakra-ui/react';
import { routes } from 'app/routing/routesConfig';
import { externalLinks } from 'app/api.config';
import { SignUpFormStep } from 'types/enums';
import { TEST_ID_LOADING_INDICATOR } from 'app/utils/test/constants';
import { useAppDispatch } from 'app/store';
import {
  signUpEmailPasswordAddedAction,
  signUpPersonalDetailsAddedAction,
} from 'app/store/Mixpanel/slice';
import { ChakraLink } from 'app/components/Core/ChakraLink';
import { AuthFormFooter } from 'app/components/AuthForm';

interface SignUpActionsProps {
  isSubmitting?: boolean;
  submitEnabled: boolean;
  step: SignUpFormStep;
  onNext: (pathname: string) => void;
  disableNextStep: boolean;
  search: string;
}

export const SignUpActions: React.FC<SignUpActionsProps> = ({
  isSubmitting = false,
  submitEnabled,
  step,
  onNext,
  disableNextStep,
  search,
}) => {
  const dispatch = useAppDispatch();
  const timeToDelay = 100;

  return (
    <VStack position="relative" my="auto" mt={12} mb={0} spacing={12}>
      {isSubmitting && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          data-testid={TEST_ID_LOADING_INDICATOR}
        >
          <CircularProgress
            isIndeterminate
            color="B.0"
            size="40px"
            thickness="7px"
          />
        </Box>
      )}
      {step === SignUpFormStep.BASIC && (
        <Button
          width="full"
          isDisabled={disableNextStep}
          data-testid="next-step-btn"
          data-step="basic"
          onClick={() => {
            setTimeout(() => {
              dispatch(signUpEmailPasswordAddedAction());
              onNext(routes.signUpDetails.path);
            }, timeToDelay);
          }}
        >
          Next
        </Button>
      )}
      {step === SignUpFormStep.DETAILS && (
        <Button
          width="full"
          my={1}
          mx={0}
          isDisabled={disableNextStep}
          data-testid="next-step-btn"
          data-step="details"
          onClick={() => {
            setTimeout(() => {
              dispatch(signUpPersonalDetailsAddedAction());
              onNext(routes.signUpPersona.path);
            }, timeToDelay);
          }}
        >
          Next
        </Button>
      )}
      {step === SignUpFormStep.USER_PERSONA && (
        <>
          <Button
            type="submit"
            width="full"
            mx={0}
            isDisabled={!submitEnabled || isSubmitting}
            data-step="create-account"
          >
            Create Account
          </Button>
        </>
      )}
      <AuthFormFooter>
        {step === SignUpFormStep.USER_PERSONA && (
          <Text
            fontWeight={400}
            fontSize="sm"
            lineHeight="24px"
            letterSpacing="0.15px"
            mb={8}
          >
            By continuing you agree to Salt Sessions
            <br />
            <Link
              href={externalLinks.signUp.TERMS_AND_CONDITIONS}
              target="_blank"
            >
              Terms &amp; Conditions
            </Link>
            {' and '}
            <Link href={externalLinks.signUp.PRIVACY_POLICY} target="_blank">
              Privacy Policy
            </Link>
          </Text>
        )}
        <VStack>
          <Text lineHeight="17px">
            Have an account?{' '}
            <ChakraLink
              to={{
                pathname: routes.auth.path,
                search,
              }}
              lineHeight="17px"
            >
              Sign In
            </ChakraLink>
          </Text>
        </VStack>
      </AuthFormFooter>
    </VStack>
  );
};
